import React from 'react';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import {
  usePatchTripDriverAckByTokenMutation,
  usePatchTripDriverAckMutation
} from '../../redux/api/trips/tripApi';
import { TripDriverAckStatus } from '../../redux/api/trips/trips.model';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import { useAppDispatch } from '../../redux/hooks';
import { setLoadDetailReload } from '../../redux/features/navigation/navigationSlice';

interface TwAcceptRejectButtonsProps {
  onDisappear: (status: TripDriverAckStatus) => void;
  driverId: string;
  tripId?: string;
  orderId?: string;
  token?: string;
}

export const TwAcceptRejectButtons: React.FC<TwAcceptRejectButtonsProps> = ({
  onDisappear,
  driverId,
  tripId,
  orderId,
  token
}: TwAcceptRejectButtonsProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { addNotification } = useNotifications();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [updateTripDriverAckByToken, { isLoading: isLoadingUpdateTripDriverAckByToken }] =
    usePatchTripDriverAckByTokenMutation();
  const [updateTripDriverAck, { isLoading: isLoadingUpdateTripDriverAck }] =
    usePatchTripDriverAckMutation();

  const handleDeclineTrip = (): void => {
    if (token) updateTripByToken(TripDriverAckStatus.REJECTED);
    if (!token) updateTrip(TripDriverAckStatus.REJECTED);
  };
  const handleAcceptTrip = (): void => {
    if (token) updateTripByToken(TripDriverAckStatus.ACCEPTED);
    if (!token) updateTrip(TripDriverAckStatus.ACCEPTED);
  };

  const updateTripByToken = (status: TripDriverAckStatus): void => {
    if (!token) return;

    updateTripDriverAckByToken({
      token,
      tripId: tripId as string,
      body: { driver_ack_status: status, driver_id: driverId }
    })
      .then(() => {
        addNotification({
          message: `Load has been ${status.toLowerCase()}`,
          variant: 'success'
        });
        onDisappear(status);
      })
      .catch(() => {
        const statusSelection = status === TripDriverAckStatus.REJECTED ? 'rejecting' : 'accepting';
        addNotification({
          message: `Something went wrong while ${statusSelection} the trip`,
          variant: 'error'
        });
      });
  };

  const updateTrip = (status: TripDriverAckStatus): void => {
    if (!tripId) return;

    updateTripDriverAck({
      tripId,
      body: { driver_ack_status: status, driver_id: driverId }
    })
      .then(() => {
        addNotification({
          message: `Load has been ${status.toLowerCase()}`,
          variant: 'success'
        });

        if (orderId) dispatch(setLoadDetailReload(true));
        onDisappear(status);
      })
      .catch(() => {
        const statusSelection = status === TripDriverAckStatus.REJECTED ? 'rejecting' : 'accepting';
        addNotification({
          message: `Something went wrong while ${statusSelection} the trip`,
          variant: 'error'
        });
      });
  };
  return (
    <Box
      sx={{ display: 'flex', gap: '12px', flexDirection: matchesMobileOrTablet ? 'column' : 'row' }}
    >
      <TwButton
        label="Decline trip"
        action={handleDeclineTrip}
        type={TwButtonType.secondary}
        disabled={isLoadingUpdateTripDriverAckByToken || isLoadingUpdateTripDriverAck}
        fullwidth={matchesMobileOrTablet}
      />
      <TwButton
        label="Accept trip"
        action={handleAcceptTrip}
        type={TwButtonType.primary}
        disabled={isLoadingUpdateTripDriverAckByToken || isLoadingUpdateTripDriverAck}
        fullwidth={matchesMobileOrTablet}
      />
    </Box>
  );
};
